import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  ArrowRight, BarChart3, Headphones, Handshake, FileText, ChevronDown,
  ShieldCheck, Clock3, Globe2, Network, Maximize2, XCircle,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";
import { CONTACT_IMAGES } from "@/lib/site";

const CONTACT_CARDS = [
  { icon: BarChart3,  title: "Sales",        email: "sales@estherarealityos.com",        lines: ["Book demos.", "Product questions.", "Enterprise inquiries."] },
  { icon: Headphones, title: "Support",      email: "support@estherarealityos.com",      lines: ["Technical help.", "Platform support.", "Account assistance."] },
  { icon: Handshake,  title: "Partnerships", email: "partnerships@estherarealityos.com", lines: ["Integrations.", "Technology alliances.", "Channel partnerships."] },
];

const ENQUIRY_TYPES = [
  "Product Demonstrations",
  "Sales Inquiries",
  "Technical Support",
  "Partnership Opportunities",
];

const TRUST = [
  { icon: ShieldCheck, title: "Enterprise", line: "Security" },
  { icon: Clock3,      title: "High",       line: "Availability" },
  { icon: Headphones,  title: "Dedicated",  line: "Support" },
  { icon: Globe2,      title: "Global",     line: "Infrastructure" },
  { icon: FileText,    title: "Compliance", line: "Ready" },
  { icon: Maximize2,   title: "Scalable",   line: "Platform" },
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", company: "", phone: "", inquiry: "", message: "" });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setScrollY(window.scrollY)); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);
  const heroParallax = `translate3d(0, ${-scrollY * 0.12}px, 0)`;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateField = (name, value) => {
    const v = (value || "").trim();
    if (name === "phone") {
      if (!v) return "Phone number is required.";
      if (!isValidPhoneNumber(v)) return "Please enter a valid phone number.";
      return "";
    }
    if (!v) {
      switch (name) {
        case "firstName": return "First name is required.";
        case "lastName":  return "Last name is required.";
        case "email":     return "Email is required.";
        case "company":   return "Company is required.";
        case "inquiry":   return "Please choose an inquiry type.";
        case "message":   return "A short message is required.";
        default: return "This field is required.";
      }
    }
    if (name === "email" && !EMAIL_RE.test(v)) return "Please enter a valid email address.";
    return "";
  };
  const errors = Object.fromEntries(Object.keys(form).map((k) => [k, validateField(k, form[k])]));
  const isValid = Object.values(errors).every((e) => !e);
  const showErr = (name) => (touched[name] || touched.__all__) && errors[name];
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const markTouched = (name) => setTouched((t) => ({ ...t, [name]: true }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) { setTouched({ __all__: true }); return; }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          phone: form.phone,
          inquiry_type: form.inquiry,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setSubmitted(true);
    } catch {
      setSubmitError("We couldn't send your message. Please try again in a moment, or email us directly at hello@estherarealityos.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = "w-full h-11 rounded-md bg-[rgba(10,15,26,0.7)] border border-white/10 px-3.5 text-[13.5px] text-platinum placeholder:text-platinum/40 focus:outline-none focus:border-sapphire-glow/55 transition-colors";
  const fieldErrStyle = "border-red-500/60 focus:border-red-400/70";
  const cls = (name, extra = "") => `${fieldStyle} ${showErr(name) ? fieldErrStyle : ""} ${extra}`;
  const fieldErr = (name) => showErr(name) ? (
    <div className="text-[11px] text-red-400/90 mt-1 pl-0.5" data-testid={`contact-err-${name}`}>{errors[name]}</div>
  ) : null;
  const reqStar = <span className="text-red-400/70 ml-0.5" aria-hidden>*</span>;

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="contact-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={32} tone="mixed" density={0.55} />
      </div>
      <Navbar />

      {/* HERO */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-12 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)" }} aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center min-h-[480px] lg:min-h-[560px]">
            <div className="fade-up relative z-10">
              <div className="eyebrow-intelligence">CONTACT ESTHERA
              </div>
              <h1 className="font-display mt-5 text-platinum text-[42px] sm:text-[52px] lg:text-[58px] xl:text-[62px] leading-[1.04] tracking-[-0.022em]">
                Let&apos;s Build The Future<br />
                <span style={{ textShadow: "0 0 30px rgba(96,165,250,0.25)" }}>Of Real Estate Together</span>
              </h1>
              <p className="mt-6 text-[15px] sm:text-[16px] text-platinum/72 leading-[1.7] max-w-[480px]">
                Whether you&apos;re exploring Esthera RealityOS, requesting a demonstration,
                seeking support, or discussing partnerships, our team is ready to help.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button onClick={() => navigate("/book-demo")} data-testid="contact-hero-book-demo"
                  className="btn-primary rounded-md h-11 px-5 text-[13.5px] font-medium inline-flex items-center gap-2">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
                <a href="#contact-form" data-testid="contact-hero-sales"
                  className="btn-ghost rounded-md h-11 px-5 text-[13.5px] font-medium inline-flex items-center gap-2">
                  Contact Sales <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* World-map network — full-bleed */}
            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="contact-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.30), transparent 72%)", filter: "blur(48px)" }} aria-hidden />
              <div className="absolute top-[24%] right-[18%] w-1.5 h-1.5 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.4s", boxShadow: "0 0 12px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[42%] left-[18%] w-1 h-1 rounded-full bg-amber-200 twinkle" style={{ animationDelay: "1.2s", boxShadow: "0 0 10px rgba(252,211,77,0.95)" }} aria-hidden />
              <div className="absolute bottom-[28%] right-[26%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "2.0s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="relative">
                <img src={CONTACT_IMAGES.hero}
                  alt="Esthera RealityOS — global connected intelligence network spanning every continent"
                  className="block w-full h-[460px] lg:h-[580px] object-cover object-center"
                  style={{
                    filter: "brightness(0.95) contrast(1.20) saturate(1.18)",
                    WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }}
                  draggable="false" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.80) 14%, rgba(10,15,26,0.16) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 contact cards */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {CONTACT_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <a key={c.title} href={`mailto:${c.email}`} data-testid={`contact-card-${i}`}
                  className="group relative rounded-2xl border border-white/[0.07] hover:border-sapphire-glow/40 p-6 lg:p-7 fade-up transition-all duration-500 min-h-[200px]"
                  style={{ background: "linear-gradient(180deg, rgba(15,22,40,0.45) 0%, rgba(10,15,26,0.85) 100%)", animationDelay: `${i * 80}ms` }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(96,165,250,0.40)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                  <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full inline-flex items-center justify-center"
                      style={{ background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.12), #0E1422 75%)", border: "1px solid rgba(96,165,250,0.55)", boxShadow: "0 0 16px -3px rgba(96,165,250,0.40)" }}>
                      <Icon className="w-5 h-5 text-sapphire-glow" />
                    </div>
                    <div className="font-display text-platinum text-[18px] tracking-tight">{c.title}</div>
                  </div>
                  <div className="text-[13px] text-sapphire-glow font-medium break-all mb-3">{c.email}</div>
                  <ul className="space-y-1 text-[12.5px] text-platinum/65">
                    {c.lines.map((ln) => <li key={ln}>{ln}</li>)}
                  </ul>
                  <ArrowRight className="absolute bottom-5 right-5 w-4 h-4 text-platinum/40 transition-all duration-300 group-hover:text-sapphire-glow group-hover:translate-x-1" />
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL REACH + MESSAGE FORM */}
      <section className="relative pb-16 lg:pb-20" id="contact-form">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
            {/* Regions hub */}
            <div className="relative rounded-2xl border border-white/[0.07] overflow-hidden p-6 lg:p-8 fade-up min-h-[480px]"
              style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)" }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
              <h2 className="font-display text-platinum text-[20px] sm:text-[22px] lg:text-[24px] leading-tight tracking-[-0.018em]">
                Built For Real Estate Organizations Worldwide
              </h2>
              <div className="relative mt-4 aspect-square overflow-hidden">
                <div className="absolute inset-[-8%] pointer-events-none"
                  style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.35), transparent 72%)", filter: "blur(48px)" }} aria-hidden />
                <img src={CONTACT_IMAGES.regions}
                  alt="Global operations hub — North America (New York · Toronto), Europe (London), Middle East (Dubai · Riyadh), Asia Pacific (Singapore · Sydney) connected to the Esthera intelligence core"
                  className="relative w-full h-full object-cover scale-[1.32] sm:scale-[1.18]"
                  style={{
                    mixBlendMode: "screen",
                    filter: "brightness(0.78) contrast(2.85) saturate(1.30)",
                    WebkitMaskImage: "radial-gradient(62% 62% at 50% 50%, #000 38%, rgba(0,0,0,0.55) 66%, transparent 90%)",
                    maskImage: "radial-gradient(62% 62% at 50% 50%, #000 38%, rgba(0,0,0,0.55) 66%, transparent 90%)",
                  }}
                  draggable="false" />
                {/* Outer vignette — fully opaque at edges so nothing beyond the mask ever peeks through (fixes 'CA'/'S' clip on mobile) */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(72% 72% at 50% 50%, transparent 45%, rgba(10,15,26,0.85) 72%, rgba(10,15,26,1) 88%, rgba(10,15,26,1) 100%)",
                  }}
                  aria-hidden />
                {/* Side edge caps — extra safety for narrow viewports */}
                <div className="absolute inset-y-0 left-0 w-[14%] pointer-events-none"
                  style={{ background: "linear-gradient(90deg, #0A0F1A 0%, rgba(10,15,26,0) 100%)" }} aria-hidden />
                <div className="absolute inset-y-0 right-0 w-[14%] pointer-events-none"
                  style={{ background: "linear-gradient(-90deg, #0A0F1A 0%, rgba(10,15,26,0) 100%)" }} aria-hidden />
              </div>
            </div>

            {/* Send Us a Message */}
            <div className="relative rounded-2xl border border-white/[0.07] overflow-hidden p-6 lg:p-8 fade-up min-h-[480px]"
              style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)", animationDelay: "100ms" }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
              <h2 className="font-display text-platinum text-[20px] sm:text-[22px] lg:text-[24px] leading-tight tracking-[-0.018em] mb-5">
                Send Us A Message
              </h2>
              <div className="grid grid-cols-[0.85fr_1.4fr] gap-5">
                {/* Inquiry pill column */}
                <ul className="space-y-3 pt-1">
                  {ENQUIRY_TYPES.map((t, i) => {
                    const Icon = [BarChart3, BarChart3, Headphones, Handshake, FileText][i] || FileText;
                    return (
                      <li key={t} className="flex items-start gap-2.5 text-[12.5px] text-platinum/70 leading-[1.4]">
                        <Icon className="w-3.5 h-3.5 mt-[3px] text-sapphire-glow shrink-0" />
                        <span>{t}</span>
                      </li>
                    );
                  })}
                </ul>
                {/* Form */}
                <form onSubmit={submit} data-testid="contact-form" className="space-y-3" noValidate>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.18em] uppercase text-platinum/55 block mb-1">First Name{reqStar}</label>
                      <input className={cls("firstName")} placeholder="e.g. Priya" required
                        value={form.firstName}
                        onChange={(e) => setField("firstName", e.target.value)}
                        onBlur={() => markTouched("firstName")}
                        data-testid="contact-first-name" />
                      {fieldErr("firstName")}
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.18em] uppercase text-platinum/55 block mb-1">Last Name{reqStar}</label>
                      <input className={cls("lastName")} placeholder="e.g. Sharma" required
                        value={form.lastName}
                        onChange={(e) => setField("lastName", e.target.value)}
                        onBlur={() => markTouched("lastName")}
                        data-testid="contact-last-name" />
                      {fieldErr("lastName")}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.18em] uppercase text-platinum/55 block mb-1">Business Email{reqStar}</label>
                      <input type="email" className={cls("email")} placeholder="you@company.com" required
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        onBlur={() => markTouched("email")}
                        data-testid="contact-email" />
                      {fieldErr("email")}
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.18em] uppercase text-platinum/55 block mb-1">Company{reqStar}</label>
                      <input className={cls("company")} placeholder="e.g. Sotheby's International Realty" required
                        value={form.company}
                        onChange={(e) => setField("company", e.target.value)}
                        onBlur={() => markTouched("company")}
                        data-testid="contact-company" />
                      {fieldErr("company")}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.18em] uppercase text-platinum/55 block mb-1">Phone{reqStar}</label>
                      <PhoneInput
                        defaultCountry="IN"
                        international
                        countryCallingCodeEditable={false}
                        placeholder="98765 43210"
                        value={form.phone}
                        onChange={(v) => setField("phone", v || "")}
                        onBlur={() => markTouched("phone")}
                        className={`esthera-phone-input sm ${showErr("phone") ? "err" : ""}`}
                        data-testid="contact-phone" />
                      {fieldErr("phone")}
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.18em] uppercase text-platinum/55 block mb-1">Inquiry Type{reqStar}</label>
                      <div className="relative">
                        <select className={cls("inquiry", "appearance-none pr-9")} required
                          value={form.inquiry}
                          onChange={(e) => { setField("inquiry", e.target.value); markTouched("inquiry"); }}
                          onBlur={() => markTouched("inquiry")}
                          data-testid="contact-inquiry">
                          <option value="" disabled>Select an option</option>
                          {ENQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-platinum/50 pointer-events-none" />
                      </div>
                      {fieldErr("inquiry")}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.18em] uppercase text-platinum/55 block mb-1">Message{reqStar}</label>
                    <textarea rows={3} className={cls("message", "!h-auto py-3 resize-none")}
                      placeholder="Tell us how we can help — e.g. Evaluating Esthera for our Mumbai luxury portfolio."
                      required
                      value={form.message}
                      onChange={(e) => setField("message", e.target.value)}
                      onBlur={() => markTouched("message")}
                      data-testid="contact-message" />
                    {fieldErr("message")}
                  </div>
                  {submitError && (
                    <div data-testid="contact-error" role="alert"
                      className="rounded-md px-3.5 py-2.5 text-[13px] leading-[1.5] flex items-start gap-2"
                      style={{ background: "rgba(220,38,38,0.10)", border: "1px solid rgba(220,38,38,0.35)", color: "#FCA5A5" }}>
                      <XCircle className="w-4 h-4 mt-[2px] shrink-0" />
                      <span>{submitError}</span>
                    </div>
                  )}
                  <button type="submit" disabled={submitting || submitted} data-testid="contact-submit"
                    className="btn-primary w-full rounded-md h-11 text-[13px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                    {submitted ? "Message sent — we'll be in touch" : submitting ? "Sending…" : <>Send Message <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <p className="text-[10.5px] text-platinum/45 leading-relaxed">Fields marked <span className="text-red-400/70">*</span> are required.</p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="relative rounded-2xl border border-white/[0.07] px-6 sm:px-8 py-6 lg:py-7"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
              {TRUST.map((t, i) => {
                const Icon = t.icon;
                return (
                  <div key={i} className="flex items-center gap-3 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                    <Icon className="w-5 h-5 text-sapphire-glow shrink-0" />
                    <div className="leading-tight">
                      <div className="text-[13px] text-platinum font-medium">{t.title}</div>
                      <div className="text-[11px] text-platinum/55">{t.line}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* READY TO SEE ESTHERA IN ACTION? */}
      <section className="relative pb-20 lg:pb-24" data-testid="contact-bottom-cta">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] min-h-[220px] lg:min-h-[260px]"
            style={{ background: "linear-gradient(135deg, rgba(13,30,62,0.55) 0%, rgba(10,15,26,0.92) 60%, rgba(10,15,26,0.95) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/55 to-transparent" />
            {/* Wave network on the right */}
            <div className="absolute inset-0 pointer-events-none">
              <img src={CONTACT_IMAGES.ctaWave} alt="" aria-hidden="true"
                className="absolute inset-y-0 right-0 h-full w-[60%] object-cover object-center"
                style={{
                  filter: "brightness(0.85) contrast(1.20) saturate(1.20)",
                  WebkitMaskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.55) 25%, rgba(0,0,0,0.95) 60%, rgba(0,0,0,0.95) 100%)",
                  maskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.55) 25%, rgba(0,0,0,0.95) 60%, rgba(0,0,0,0.95) 100%)",
                }}
                draggable="false" />
              <div className="absolute inset-0"
                style={{ background: "linear-gradient(90deg, rgba(10,15,26,0.92) 0%, rgba(10,15,26,0.55) 30%, rgba(10,15,26,0.10) 65%, rgba(10,15,26,0) 100%)" }} />
            </div>
            <div className="relative z-10 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center p-8 sm:p-10 lg:p-12">
              <div className="fade-up">
                <h3 className="font-display text-platinum text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.12] tracking-[-0.018em]">
                  Ready To See <span className="text-sapphire-glow" style={{ textShadow: "0 0 30px rgba(96,165,250,0.45)" }}>Esthera In Action?</span>
                </h3>
                <p className="mt-3 text-[14px] text-platinum/72 leading-[1.65] max-w-[480px]">
                  Discover how intelligent operations can transform your organization.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 fade-up" style={{ animationDelay: "100ms" }}>
                <button onClick={() => navigate("/book-demo")} data-testid="contact-cta-book-demo"
                  className="btn-primary rounded-md h-11 px-5 text-[13.5px] font-medium inline-flex items-center gap-2">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => navigate("/platform")} data-testid="contact-cta-explore"
                  className="btn-ghost rounded-md h-11 px-5 text-[13.5px] font-medium inline-flex items-center gap-2">
                  Explore Platform <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
